/**
 * Adapted from PlayCanvas Engine's gsplat-sog-resource.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type { GSplatSogData } from '@/plugins/gsplat/core/GSplatSogData'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat, type GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import {
  GSplatResourceBase,
  type GSplatResourceBaseOptions,
  type GSplatResourceDataSource,
} from '@/plugins/gsplat/unified/resource/GSplatResourceBase'

type SplatShapeReader = NonNullable<
  GSplatResourceBaseOptions<GSplatSogData, unknown>['readSplatShape']
>
type SplatColorReader = NonNullable<
  GSplatResourceBaseOptions<GSplatSogData, unknown>['readSplatColor']
>

const READ_GLSL = '#include "gsplatSogVS"'
const READ_WGSL = '#include "gsplatSogVS"'

export interface GSplatSogResourceOptions<TData extends GSplatResourceDataSource, TIterator>
  extends GSplatResourceBaseOptions<TData, TIterator> {
  /** SOG source textures 仍归 data 所有；这里只保存其一次性释放入口。 */
  readonly releaseTextureSources?: (data: TData) => void
}

export type GSplatSogResourceCreateOptions = Omit<
  GSplatSogResourceOptions<GSplatSogData, unknown>,
  'adapter' | 'data' | 'format' | 'parameters' | 'defines' | 'releaseTextureSources'
>

function createFormat(adapter: GSplatGraphicsAdapter, data: GSplatSogData): GSplatFormat {
  const streams: GSplatStreamDescriptor[] = [
    { name: 'means_l', format: 'rgba8unorm' },
    { name: 'means_u', format: 'rgba8unorm' },
    { name: 'quats', format: 'rgba8unorm' },
    { name: 'scales', format: 'rgba8unorm' },
    { name: 'sh0', format: 'rgba8unorm' },
  ]
  if (data.shBands > 0) {
    streams.push({ name: 'sh_labels', format: 'rgba8unorm' })
    streams.push({ name: 'sh_centroids', format: 'rgba8unorm' })
  }
  if (data.meta.version === 2) streams.push({ name: 'sogCodebook', format: 'rgba32float' })
  return new GSplatFormat(adapter, streams, {
    readGLSL: READ_GLSL,
    readWGSL: READ_WGSL,
  })
}

function createParameters(data: GSplatSogData): ReadonlyMap<string, unknown> {
  const { meta } = data
  const result = new Map<string, unknown>([
    ['means_mins', meta.means.mins],
    ['means_maxs', meta.means.maxs],
  ])
  if (meta.version === 1) {
    result.set('scales_mins', meta.scales.mins)
    result.set('scales_maxs', meta.scales.maxs)
    result.set('sh0_mins', meta.sh0.mins)
    result.set('sh0_maxs', meta.sh0.maxs)
    if (meta.shN) {
      result.set('shN_mins', meta.shN.mins)
      result.set('shN_maxs', meta.shN.maxs)
    }
  }
  return result
}

function createDefines(data: GSplatSogData): ReadonlyMap<string, unknown> {
  const result = new Map<string, unknown>([['SH_BANDS', data.shBands]])
  if (data.meta.version === 2) result.set('SOG_V2', '')
  return result
}

/** SOG JSON/bundle 的 packed source Resource，不展开为普通 GSplatData。 */
export class GSplatSogResource<
  TData extends GSplatResourceDataSource = GSplatSogData,
  TIterator = unknown,
> extends GSplatResourceBase<TData, TIterator> {
  readonly kind = 'sog' as const
  private readonly releaseTextureSourcesCallback: ((data: TData) => void) | null
  private textureSourcesReleased = false

  constructor(options: GSplatSogResourceOptions<TData, TIterator>) {
    super(options)
    this.releaseTextureSourcesCallback = options.releaseTextureSources ?? null
  }

  private static createShapeReader(data: GSplatSogData): SplatShapeReader {
    const position = new THREE.Vector3()
    const rotation = new THREE.Quaternion()
    const logScale = new THREE.Vector3()
    const iterator = data.createIter(position, rotation, logScale)
    return (index, targetPosition, targetRotation, targetRadii) => {
      if (!Number.isSafeInteger(index) || index < 0 || index >= data.numSplats) return false
      iterator.read(index)
      const rx = Math.exp(logScale.x)
      const ry = Math.exp(logScale.y)
      const rz = Math.exp(logScale.z)
      if (
        !Number.isFinite(position.x) ||
        !Number.isFinite(position.y) ||
        !Number.isFinite(position.z) ||
        !Number.isFinite(rx) ||
        !Number.isFinite(ry) ||
        !Number.isFinite(rz) ||
        rx <= 0 ||
        ry <= 0 ||
        rz <= 0
      ) {
        return false
      }
      targetPosition.copy(position)
      targetRotation.copy(rotation).normalize()
      if (targetRotation.w < 0) {
        targetRotation.set(
          -targetRotation.x,
          -targetRotation.y,
          -targetRotation.z,
          -targetRotation.w
        )
      }
      targetRadii.set(rx, ry, rz)
      return true
    }
  }
  private static createColorReader(data: GSplatSogData): SplatColorReader {
    const color = new THREE.Vector4()
    const iterator = data.createIter(undefined, undefined, undefined, color)
    return (index, targetColor) => {
      if (!Number.isSafeInteger(index) || index < 0 || index >= data.numSplats) return false
      iterator.read(index)
      if (!Number.isFinite(color.x) || !Number.isFinite(color.y) || !Number.isFinite(color.z)) {
        return false
      }
      targetColor.copy(color)
      return true
    }
  }

  /**
   * 构造共享 Data/Resource 对象模型；backend 差异只存在于 adapter 创建的 texture leaf。
   * 调用前必须完成 V2 codebook 与可选 centers 的异步 preparation。
   */
  static create(
    adapter: GSplatGraphicsAdapter,
    data: GSplatSogData,
    options: GSplatSogResourceCreateOptions = {}
  ): GSplatSogResource<GSplatSogData> {
    if (data.adapter !== adapter) {
      throw new Error('GSplatSogResource: adapter 必须与 GSplatSogData 的 texture owner 相同')
    }
    if (!data.isPreparedForGeneration()) {
      throw new Error('GSplatSogResource: V2 data 尚未为当前 graphics generation 准备 codebook')
    }
    const format = createFormat(adapter, data)
    const streams = new GSplatStreams(adapter)
    try {
      streams.attachBorrowed(
        format,
        { width: data.means_l.width, height: data.means_l.height },
        data.getSourceTextures()
      )
      const resource = new GSplatSogResource<GSplatSogData>({
        ...options,
        adapter,
        data,
        format,
        parameters: createParameters(data),
        defines: createDefines(data),
        createIterator: (value) => value.createIter(),
        readSplatShape: options.readSplatShape ?? GSplatSogResource.createShapeReader(data),
        readSplatColor: options.readSplatColor ?? GSplatSogResource.createColorReader(data),
        disposeData: (value) => value.destroy(),
        releaseTextureSources: (value) => value.releaseTextureSources(),
      })
      resource.attachStreams(streams)
      return resource
    } catch (error) {
      streams.destroy()
      throw error
    }
  }

  override get supportsWorkBufferGeometry(): boolean {
    return true
  }

  releaseTextureSources(): void {
    this.assertActive()
    if (this.textureSourcesReleased) return
    this.textureSourcesReleased = true
    this.releaseTextureSourcesCallback?.(this.data)
  }

  protected override actualDestroy(): void {
    if (this.isDestroyed) return
    // Data-owned leaves 必须先从 Streams 脱离，随后由 base 的 disposeData 唯一销毁。
    this.streams?.detachBorrowed()
    super.actualDestroy()
  }
}
